import {useSyncExternalStore} from 'react'
import {plane2dIcon} from '@img/index.js'

/**
 * "FLIGHT IS ON THE WAY" — the welcome screen BEFORE the aircraft is down.
 *
 * The rest of that screen ("Welcome! You've arrived", the walk to the booth, the driver
 * card) is written for a passenger standing in an arrivals hall. Shown to someone still in
 * the air it is not merely early, it is wrong: it tells them a driver is waiting and offers
 * to walk them somewhere they cannot go. So until the flight reports down, this replaces
 * it — one heading and the flight's own progress, and nothing that invites an action.
 *
 * ── ⚠️ THE CARD IS THE EXISTING DESIGN, NOT A NEW ONE ─────────────────────────────
 * Airport name with its IATA code above a large time, both ends, a dashed track between
 * them with the plane on a mint disc. Same markup and the same `plane2dIcon` as
 * `FlightCard` in the driver portal's `features/fromAirportPickup/parts.jsx` — the two
 * screens show one passenger the same flight, and a second flight card that merely
 * resembles the first is how a design stops being one.
 *
 * The "is it down yet" rule lives in `features/fromAirportArrival/flightStatus.js` — both
 * this screen and the hook that polls for it read the same one.
 *
 * ── ⚠️ THE PLANE'S POSITION IS TIME, NOT POSITION ──────────────────────────────────
 * It is the share of the flight that has elapsed — we have no aircraft position here and
 * are not pretending to. Clamped to 2–98%: a plane pinned to either end reads as "not
 * departed" or "landed", and both are claims this screen must not make while the status
 * still says otherwise.
 */

/**
 * WHEN THIS END OF THE FLIGHT HAPPENS — the truest time the provider has given us.
 *
 * ⚠️ AERODATABOX DOES NOT SEND THE SAME FIELDS AT BOTH ENDS, AND READING TOO FEW BREAKS
 * MORE THAN A LABEL. This asked for `revisedTime || scheduledTime` only, and a live payload
 * arrived whose DEPARTURE carried neither — only `predictedTime`. Three things followed:
 *
 *   · the departure clock read "—";
 *   · `pct` needs BOTH ends, so the flown line was never drawn;
 *   · and a null `pct` parks the aircraft mid-track, so a flight four hours out looked half
 *     flown on a fully dashed line — indistinguishable from a broken widget.
 *
 * ⚠️ THE ORDER IS "WHAT HAPPENED" BEFORE "WHAT IS EXPECTED". `runwayTime` is wheels up or
 * wheels down and `actualTime` is off or on blocks, so either beats an estimate. Then
 * `revisedTime`, the airline's own correction; then the provider's `predictedTime`; and
 * `scheduledTime` last — on a delayed flight the schedule is already in the past, which
 * would drive the plane to the far end while the aircraft is still hours out.
 */
function endTime(end) {
    const raw = end?.runwayTime?.utc
        || end?.actualTime?.utc
        || end?.revisedTime?.utc
        || end?.predictedTime?.utc
        || end?.scheduledTime?.utc
    if (!raw) return null
    /* 'YYYY-MM-DD HH:mmZ' — a bare space-separated string is parsed as LOCAL by every
       browser, which on this side of the world is hours of error. */
    const parsed = new Date(String(raw).trim().replace(' ', 'T').replace(/Z?$/, 'Z'))
    return Number.isFinite(parsed.getTime()) ? parsed : null
}

/**
 * WHERE THE AIRCRAFT IS WHEN THE TIMES CANNOT SAY — read off the provider's status word.
 *
 * ⚠️ A LANDED FLIGHT LOSES ITS DEPARTURE TIME, and that is the case this exists for. Once
 * AeroDataBox moves a flight to `Arrived` it stops sending any departure time at all — a
 * live payload for DL7064 arrived with `departure: {airport, quality}` and nothing else,
 * where the same flight an hour earlier had carried `predictedTime`. No chain of fallbacks
 * reads a field that is not there.
 *
 * The status still can: a flight that has ARRIVED is 100% flown whatever the clock says, and
 * one that has not left is 0%. Both are facts rather than estimates, which is why this may
 * answer where an invented time may not.
 *
 * ⚠️ EVERYTHING ELSE STAYS NULL — "in the air with no times" has no honest fraction.
 *
 * Mirrors `progressFromStatus` in the driver portal's `features/fromAirportPickup/parts.jsx`:
 * one flight, two screens, and they must not disagree about where it is.
 */
const ARRIVED_WORDS = new Set(['arrived', 'landed'])
/** AeroDataBox spells it "Canceled", plus an "uncertain" variant; both count. */
const CANCELLED_WORDS = new Set(['canceled', 'cancelled', 'canceleduncertain'])
const NOT_DEPARTED_WORDS = new Set(['expected', 'scheduled', 'checkin', 'gateclosed', 'boarding'])

function progressFromStatus(raw) {
    const word = String(raw || '').trim().toLowerCase()
    if (ARRIVED_WORDS.has(word)) return 100
    if (NOT_DEPARTED_WORDS.has(word)) return 0
    return null
}

const clockLabel = (date) =>
    date ? date.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'}) : '—'

/** "Toronto (YYZ)", or whichever half the provider gave us. */
function placeLabel(end) {
    const name = end?.airport?.municipalityName || end?.airport?.name
    const code = end?.airport?.iata
    if (name && code) return `${name} (${code})`
    return name || code || '—'
}

/** One end of the flight — the airport and its local time. Mirrors the driver's FlightEnd. */
/* The provider's status word, phrased for a person — the driver portal's `humanStatus`
   (features/fromAirportPickup/parts.jsx), kept identical so both sides say the same thing. */
const STATUS_WORDS = {
    enroute: 'In the air',
    arrived: 'Landed',
    departed: 'Taken off',
    expected: 'Scheduled',
    approaching: 'Approaching',
    gateclosed: 'Gate closed',
    checkin: 'Check-in open',
    canceled: 'Cancelled',
    cancelled: 'Cancelled',
    canceleduncertain: 'Cancellation unconfirmed',
}

function humanStatus(raw) {
    const word = String(raw || '').trim()
    if (!word || word.toLowerCase() === 'unknown') return null
    const known = STATUS_WORDS[word.toLowerCase()]
    if (known) return known
    const spaced = word.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

function FlightEnd({end, at, align = 'left'}) {
    return (
        <div className={`min-w-0 max-md:flex-1 ${align === 'right' ? 'text-right max-md:order-2' : 'max-md:order-1'}`}>
            <p className="mb-0! break-words text-(--text-primary) max-md:text-sm">{placeLabel(end)}</p>
            {/* ⚠️ NO TIME, NO LINE — not an em dash. A landed flight's departure time is
                dropped by the provider entirely (see `progressFromStatus`), and a dash under
                an airport name reads as a field that failed to load rather than one that was
                never sent. The airport alone is the honest answer. */}
            {at && <p className="mt-1 mb-0! text-2xl font-bold text-(--text-primary) max-md:text-lg">{clockLabel(at)}</p>}
        </div>
    )
}


/**
 * "UPDATED 4 MIN AGO" — how fresh the backend's own reading of this flight is.
 *
 * ⚠️ `last_polled_at`, NOT THIS PAGE'S POLL. The page re-reads the trip every minute, but
 * the backend only asks the flight provider every ~15 min inside the watch window (and once
 * a day before it). So "the page refreshed" says nothing about the flight; this does. It is
 * ISO with an offset, so `new Date` resolves it to the right instant without the app-zone
 * parser a bare wall-clock string would need.
 */
/* The clock as an external store — see where it is read. A minute is the finest anything
   on this card shows, so that is the rate it ticks and the resolution it reports. */
const MINUTE_MS = 60000
const subscribeToMinutes = (onTick) => {
    const id = setInterval(onTick, MINUTE_MS)
    return () => clearInterval(id)
}
const minuteNow = () => Math.floor(Date.now() / MINUTE_MS)

function updatedAgo(raw, now) {
    if (!raw) return null
    const at = new Date(raw)
    if (!Number.isFinite(at.getTime())) return null
    const minutes = Math.max(0, Math.floor((now - at.getTime()) / 60000))
    if (minutes < 1) return 'Updated just now'
    if (minutes < 60) return `Updated ${minutes} min ago`
    const hours = Math.floor(minutes / 60)
    return `Updated ${hours} hr${hours === 1 ? '' : 's'} ago`
}

/**
 * @param {boolean} [embedded] rendered inside `WelcomeModal`, whose HEADER already carries
 *   this screen's title. The <h2> is dropped in that case: the modal showed "Flight is on
 *   the way" twice, once in the header bar and again two lines below it.
 */
export default function FlightOnTheWay({flight, flightNumber, summary = null, trackingFailed = false, embedded = false}) {
    /**
     * NOW, TO THE MINUTE — and it is also what re-renders the track.
     *
     * A passenger leaves this screen open for the length of a flight, so without a timer the
     * aircraft is frozen where it was when the page loaded and "Updated 4 min ago" stays
     * four minutes ago for an hour.
     *
     * ⚠️ THE TICK IS THE CLOCK, NOT A NUDGE. This was a counter whose only job was to
     * force a re-render, with `Date.now()` read further down the body — an impure call
     * during render that React's own lint rejects, and which means two reads in one render
     * can disagree. Taken as a store the value IS the time, the same for every line that
     * uses it, and the interval is simply what publishes a new one.
     *
     * ⚠️ FLOORED TO THE MINUTE, because `getSnapshot` must return the same value until
     * something really changes — a raw `Date.now()` differs on every call and React
     * re-renders forever chasing it. A minute is the resolution this card shows anyway.
     */
    const now = useSyncExternalStore(subscribeToMinutes, minuteNow) * MINUTE_MS

    const departure = endTime(flight?.departure)
    const arrival = endTime(flight?.arrival)

    /**
     * The flown share, 0–100 — or null when the times are unknown.
     *
     * ⚠️ NOT DEPARTED IS 0, NOT "UNKNOWN". Before take-off the elapsed time is negative;
     * that is a real answer (no progress), so the aircraft sits at the departure end with
     * nothing filled behind it, and the bar grows from there. An earlier version treated it
     * as unknown and centred the plane, which showed a flight apparently half-way across
     * with no travelled line — indistinguishable from the widget being broken.
     *
     * ⚠️ NULL IS A DIFFERENT THING and stays different: with a missing time there is no
     * honest fraction at all, so the plane sits mid-track on a fully dashed line, saying
     * only "this flight, these two airports".
     */
    /* ⚠️ THE TIMES FIRST, THE STATUS WORD ONLY WITHOUT THEM. The provider's status can go
       stale (WS430 sat on "Boarding" hours after take-off), which pinned the plane at 0%.
       The clock does not go stale. The word is kept for when the times are gone — once a
       flight has landed the provider drops its departure time, and "Arrived" is 100%.
       Same rule as the driver portal's `flightProgress`. */
    /* ⚠️ BUT A CANCELLED OR DIVERTED FLIGHT IS NEVER FLOWN BY THE CLOCK — its times still
       exist, and would draw a plane crossing to an airport it is not flying to. Cancelled
       never left (0); diverted has no honest fraction toward THIS airport (null). */
    const statusWord = String(summary?.flight_status || flight?.status || '').trim().toLowerCase()
    let pct = null
    if (CANCELLED_WORDS.has(statusWord)) {
        pct = 0
    } else if (statusWord === 'diverted') {
        pct = null
    } else if (departure && arrival && arrival > departure) {
        const share = ((now - departure.getTime()) / (arrival.getTime() - departure.getTime())) * 100
        pct = Math.min(100, Math.max(0, share))
    } else {
        pct = progressFromStatus(summary?.flight_status || flight?.status)
    }

    const delayed = String(flight?.status || '').trim().toLowerCase() === 'delayed'
    const statusLabel = humanStatus(summary?.flight_status || flight?.status)
    const number = flight?.number || flightNumber
    const freshness = updatedAgo(summary?.last_polled_at, now)

    /**
     * ⚠️ TRACKING FAILED — said plainly, instead of a card that pretends.
     *
     * `tracking_status: failed` (with `stopped_reason: flight_not_found`) means the backend
     * could not resolve this flight number at all. There is no aircraft to draw, no landing
     * to wait for, and the page will NOT update on its own — so the in-air card, which
     * promises exactly that, would be the wrong thing to show. Before this branch existed
     * the page fell through to "Welcome! You've arrived", which was worse still.
     *
     * The pickup itself is unaffected: the ride stays booked at its own time, which is the
     * one thing a passenger in this state needs to hear.
     */
    /**
     * ⚠️ TRACKING STOPPED — ANY `tracking_status` OTHER THAN `active` (API changes Sept 2026, §8).
     *
     * The record will never refresh again: a cancelled or diverted flight, or a ride that was
     * cancelled, sets `stopped_reason` and the backend stops polling. "Flight is on the way ·
     * updated 40 minutes ago" is then a promise the page cannot keep — that is the "landed an
     * hour ago but still on the way" report. `failed` keeps its own wording above; every other
     * non-active status gets this one, with the backend's reason and no freshness line.
     */
    const trackingStatus = typeof summary?.tracking_status === 'string' ? summary.tracking_status.trim().toLowerCase() : ''
    const trackingStopped = !trackingFailed && trackingStatus !== '' && trackingStatus !== 'active'
    if (trackingStopped) {
        const reason = String(summary?.stopped_reason || '').replace(/_/g, ' ').trim()
        return (
            <div className="flex flex-col gap-6 max-md:gap-4">
                <div className="text-center">
                    {!embedded && <h2 className="mb-2! max-md:mb-1! max-md:text-lg!">We&apos;re no longer tracking this flight</h2>}
                    <p className="mx-auto mb-0! max-w-xl text-(--text-secondary)">
                        {number ? `Tracking for flight ${number} has stopped` : 'Tracking for your flight has stopped'}
                        {reason ? ` (${reason}).` : '.'}
                        {' '}Your pickup is still booked for its scheduled time.
                    </p>
                </div>
            </div>
        )
    }

    if (trackingFailed) {
        return (
            <div className="flex flex-col gap-6 max-md:gap-4">
                <div className="text-center">
                    {!embedded && <h2 className="mb-2! max-md:mb-1! max-md:text-lg!">We couldn&apos;t track this flight</h2>}
                    <p className="mx-auto mb-0! max-w-xl text-(--text-secondary)">
                        {number
                            ? `We couldn't find flight ${number}, so we can't follow its landing. Your pickup is still booked for its scheduled time.`
                            : "We couldn't find your flight, so we can't follow its landing. Your pickup is still booked for its scheduled time."}
                    </p>
                    {freshness && (
                        <p className="mt-2 mb-0! text-sm text-(--text-secondary)">{freshness}</p>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 max-md:gap-4">
            <div className="text-center">
                {!embedded && <h2 className="mb-2! max-md:mb-1! max-md:text-lg!">Flight is on the way</h2>}
                <p className="mx-auto mb-0! max-w-xl text-(--text-secondary)">
                    {number
                        ? `We're tracking ${number}. This page updates on its own once you land — there's nothing to do until then.`
                        : "We're tracking your flight. This page updates on its own once you land — there's nothing to do until then."}
                </p>
                {/* Under the promise it qualifies: "updates on its own" is only as fresh as
                    the backend's last look at the flight. */}
                {freshness && (
                    <p className="mt-2 mb-0! text-sm text-(--text-secondary)">{freshness}</p>
                )}
            </div>

            <div className="white-global-section max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color)">
                <div className="flex items-center gap-3 max-md:flex-wrap max-md:gap-y-2">
                    <FlightEnd end={flight?.departure} at={departure}/>

                    {/* The track. The flown share is drawn only when BOTH times are known —
                        with one missing there is no honest fraction, so the line stays
                        dashed end to end and the plane sits centred, which says "this
                        flight, these two airports" and nothing more. */}
                    {/* ⚠️ THE LINE IS A FLEX CHILD, NOT AN ABSOLUTE ONE, and the plane is
                        centred with an inline transform.

                        Both were `absolute top-1/2 -translate-y-1/2` — the driver portal's
                        markup — and the disc rendered ABOVE the line here rather than on it.
                        Whatever the cause (utility ordering, a translate that did not land),
                        it is not worth diagnosing when the alignment can simply be made
                        unconditional: `items-center` puts the rule on the centre line, and
                        `translate(-50%, -50%)` as a style attribute centres the disc on its
                        own position with nothing left to override it. */}
                    <div className="flex min-w-10 flex-1 flex-col max-md:order-3 max-md:basis-full">
                    <div className="relative flex h-10 w-full items-center max-md:h-8">
                        {/* The whole route, dashed, end to end. */}
                        <div className="w-full border-t-2 border-dashed border-(--border-color)"/>

                        {/* ⚠️ THE TRAVELLED LINE SPANS THE FULL TRACK, THE AIRCRAFT DOES NOT.
                            They used to share one inset container, 20px in at each end, and
                            that left a dashed 20px stub between the departure airport and the
                            start of the green — a flight that had visibly not left the ground
                            it had already left. The INSET exists for the disc alone: 40px wide
                            and centred on its position, at 0% or 100% it would hang over the
                            airport labels. A line has no width to hang, so it starts where the
                            dashed line starts and ends under the plane's centre — which is
                            what `calc` reproduces: the disc's half-width, plus the flown share
                            of the inset run between the two parked positions. */}
                        {pct !== null && (
                            <div
                                className="absolute top-1/2 left-0 border-t-2 border-(--accent-green)"
                                style={{
                                    width: `calc(20px + (100% - 40px) * ${pct / 100})`,
                                    transform: 'translateY(-50%)',
                                }}
                            />
                        )}

                        <div className="absolute inset-x-5 top-1/2 h-0">
                            <span
                                className={`absolute top-0 flex h-10 w-10 items-center justify-center rounded-full max-md:h-8 max-md:w-8 ${
                                    delayed ? 'bg-[#F38383]' : 'bg-(--accent-green-light)'
                                }`}
                                style={{left: `${pct ?? 50}%`, transform: 'translate(-50%, -50%)'}}
                            >
                            {/* ⚠️ `brightness-0` — the disc is mint or pink, never dark, so the
                                glyph on it must always be black. These icons are drawn for
                                mixed surfaces and can arrive near-white, which vanishes on
                                the mint. Same trick, same reason, as the driver portal's. */}
                                <img src={plane2dIcon} alt="" className="w-5 object-contain brightness-0 max-md:w-4"/>
                            </span>
                        </div>
                    </div>
                    {/* The status UNDER the track, centred — it is about the aircraft, not
                        either airport. Nothing drawn before the first poll has one. */}
                    {statusLabel && (
                        <p className={`mt-1 mb-0! text-center text-xs font-semibold ${
                            delayed ? 'text-(--text-red)' : 'text-(--text-secondary)'
                        }`}>
                            {statusLabel}
                        </p>
                    )}
                    </div>

                    <FlightEnd end={flight?.arrival} at={arrival} align="right"/>
                </div>
            </div>
        </div>
    )
}
